import { Button } from "@/shared/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
	Sparkles,
	Copy,
	Undo2,
	HelpCircle,
	ChevronUp,
	ChevronDown,
} from "lucide-react";
import { SearchablePicker } from "@/shared/components/SearchablePicker";

import { useMemo, useState } from "react";
import toast from "react-hot-toast";

import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { Calendar } from "@/shared/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { logger } from "@/shared/utils/logger";
import { useApi } from "@/shared/utils/request";
import {
	isPregODonnell,
	isTaylorDay,
	isNamanHowell,
	isActivityRequired,
	isTaskRequired,
} from "@/shared/utils/firm";
import RedWordTextarea from "./RedWordTextarea";
import RedWordConfirmationDialog from "./RedWordConfirmationDialog";
import { detectRedWords, hasRedWords } from "@/shared/utils/redWords";
import { getAbbreviations } from "@/shared/constants/abbreviations";
import { useSettings } from "@/shared/contexts/SettingsContext";

type Matter = {
	matter: string;
	matter_id: string;
	client: string;
	description: string;
	task_group: string;
	activity_group: string;
};

/**
 * Checks if a matter belongs to Zurich (requires 0.05 unit increments instead of 0.1)
 */
function isZurichMatter(client: string): boolean {
	if (!client) return false;
	return client.toLowerCase().includes("zurich");
}

type Task = {
	task: string;
	task_description: string;
	task_group: string;
};

type Activity = {
	activity: string;
	activity_description: string;
	activity_group: string;
};

type AddEntryDialogProps = {
	onSave: (
		newEntry: {
			id: number;
			date: string;
			matter: string;
			matter_id: string;
			description: string;
			duration: number;
			task: string;
			activity: string;
		},
		shouldExport: boolean,
	) => void;

	matters: Matter[];
	tasks: Task[];
	activities: Activity[];
	currentDate: Date;
	isInsuranceDefenseModeRestricted?: boolean;
	checkForRedWords?: boolean;
	showTrigger?: boolean;
	hardcodedMatter?: string;
};

export type AddEntryDialogHandle = {
	openWithDuration: (units: number) => void;
};

const AddEntryDialog = React.forwardRef<
	AddEntryDialogHandle,
	AddEntryDialogProps
>(
	(
		{
			onSave,
			matters,
			tasks,
			activities,
			currentDate,
			isInsuranceDefenseModeRestricted = false,
			checkForRedWords = false,
			showTrigger = false,
			hardcodedMatter = "",
		},
		ref,
	) => {
		// export default function AddEntryDialog({
		//   onSave,
		//   matters,
		//   tasks,
		//   activities,
		// }: AddEntryDialogProps) {
		const [formValues, setFormValues] = useState({
			id: 0,
			date: "",
			matter: "",
			matter_id: "",
			description: "",
			duration: 0,
			task: "",
			activity: "",
		});

		const [open, setOpen] = React.useState(false);

		const [date, setDate] = React.useState<Date>(currentDate || new Date());

		React.useEffect(() => {
			setDate(currentDate || new Date());
		}, [currentDate]);

		// Clear undo state when dialog closes
		React.useEffect(() => {
			if (!open) {
				setPreviousDescription(null);
			}
		}, [open]);

		// Update matter when hardcodedMatter changes
		React.useEffect(() => {
			if (hardcodedMatter) {
				// Find the matter object by matter_id
				const selectedMatter = matters.find(
					(m) => m.matter_id === hardcodedMatter,
				);
				if (selectedMatter) {
					setFormValues((prev) => ({
						...prev,
						matter: selectedMatter.matter,
						matter_id: selectedMatter.matter_id,
					}));
					// Also update display state so dropdown shows the selected matter
					setValue(selectedMatter.matter_id);
				}
			} else {
				// Clear both form and display state when hardcoded matter is removed
				setFormValues((prev) => ({
					...prev,
					matter: "",
					matter_id: "",
				}));
				setValue("");
			}
		}, [hardcodedMatter, matters]);

		const { darkMode } = useDarkMode();
		const { settings } = useSettings();

		const [showRedWordDialog, setShowRedWordDialog] = useState(false);
		const [isFixingRedWords, setIsFixingRedWords] = useState(false);
		const [isGenerating, setIsGenerating] = useState(false);
		const [isDuplicating, setIsDuplicating] = useState(false);
		const [previousDescription, setPreviousDescription] = useState<
			string | null
		>(null);
		const [aiGenerationUsed, setAiGenerationUsed] = useState(false); // Track if AI generation was used this session
		const [pendingSave, setPendingSave] = useState<{
			shouldExport: boolean;
			redWordMatches: ReturnType<typeof detectRedWords>;
		} | null>(null);

		const shouldHighlightRedWords =
			isInsuranceDefenseModeRestricted && checkForRedWords;
		const { makeApiRequest } = useApi();

		React.useImperativeHandle(
			ref,
			() => ({
				openWithDuration: (units: number) => {
					// Find the matter object by matter_id from hardcodedMatter
					const selectedMatter = hardcodedMatter
						? matters.find((m) => m.matter_id === hardcodedMatter)
						: null;

					setFormValues((prev) => ({
						...prev,
						duration: units,
						matter: selectedMatter?.matter || "",
						matter_id: selectedMatter?.matter_id || "",
					}));
					// Sync display state with form state (always update, including clearing)
					setValue(selectedMatter?.matter_id || "");
					setOpen(true);
				},
			}),
			[hardcodedMatter, matters],
		);

		const availableMatters = useMemo(() => {
			return matters.map((matter) => ({
				matter: matter.description,
				client: matter.client,
				name: matter.matter,
				matter_id: matter.matter_id,
				combined: `${matter.matter} | ${matter.description} - ${matter.client}`,
				combinedTrimmed: `${matter.matter.trim()} | ${matter.description.trim()} - ${matter.client.trim()}`, // Trimmed for display
			}));
		}, [matters]);

		const [searchTerm, setSearchTerm] = useState("");
		const [taskSearchTerm, setTaskSearchTerm] = useState("");
		const [activitySearchTerm, setActivitySearchTerm] = useState("");
		const filteredMatters = useMemo(() => {
			if (!searchTerm) {
				return availableMatters;
			}

			const query = searchTerm.toLowerCase();

			// Score each matter based on match quality, then limit to top 20
			return availableMatters
				.map((matter) => {
					let score = 0;
					const matterName = matter.matter.toLowerCase();
					const name = matter.name.toLowerCase();
					const client = matter.client.toLowerCase();

					// Exact match gets highest score
					if (matterName === query || name === query) score += 100;
					else if (matterName.startsWith(query) || name.startsWith(query))
						score += 50;
					else if (matterName.includes(query) || name.includes(query))
						score += 25;

					if (client === query) score += 80;
					else if (client.startsWith(query)) score += 40;
					else if (client.includes(query)) score += 20;

					return { matter, score };
				})
				.filter((item) => item.score > 0)
				.sort((a, b) => b.score - a.score)
				.slice(0, 20)
				.map((item) => item.matter);
		}, [searchTerm, availableMatters]);

		// Taylor Day default groups
		const TAYLOR_DAY_DEFAULT_TASK_GROUP =
			"9a69ea86-d958-482c-b2fd-35c6ee47830d";
		const TAYLOR_DAY_DEFAULT_ACTIVITY_GROUP =
			"dca1c471-21cb-4c00-8e62-d075d854446d";

		// Naman Howell default task group
		const NAMAN_HOWELL_DEFAULT_TASK_GROUP =
			"54bc344d-0b93-93e4-c912-69a39322a816";

		const availableTasks = useMemo(() => {
			// For Taylor Day: filter tasks by the selected matter's task_group
			if (isTaylorDay()) {
				const selectedMatter = matters.find(
					(matter) => matter.matter_id === formValues.matter_id,
				);

				// Case 1: No matter selected - show default task_group
				if (!selectedMatter) {
					return tasks
						.filter((task) => task.task_group === TAYLOR_DAY_DEFAULT_TASK_GROUP)
						.map((task) => ({
							task: task.task,
							desc: task.task_description,
							task_group: task.task_group,
							combined: `${task.task} - ${task.task_description}`,
						}));
				}

				// Case 2: Matter selected but task_group is NULL/empty - return empty (task not required)
				if (
					!selectedMatter.task_group ||
					selectedMatter.task_group.trim() === ""
				) {
					return [];
				}

				// Case 3: Matter selected with valid task_group - show only that group
				return tasks
					.filter((task) => task.task_group === selectedMatter.task_group)
					.map((task) => ({
						task: task.task,
						desc: task.task_description,
						task_group: task.task_group,
						combined: `${task.task} - ${task.task_description}`,
					}));
			}

			// For Naman Howell: filter tasks by task_group, not required when NULL
			if (isNamanHowell()) {
				const selectedMatter = matters.find(
					(matter) => matter.matter_id === formValues.matter_id,
				);

				// No matter selected - show default task group
				if (!selectedMatter) {
					return tasks
						.filter((task) => task.task_group === NAMAN_HOWELL_DEFAULT_TASK_GROUP)
						.map((task) => ({
							task: task.task,
							desc: task.task_description,
							task_group: task.task_group,
							combined: `${task.task} - ${task.task_description}`,
						}));
				}

				// Matter selected but task_group is NULL - not required, return empty
				if (
					!selectedMatter.task_group ||
					selectedMatter.task_group.trim() === ""
				) {
					return [];
				}

				// Matter selected with valid task_group - show only that group
				return tasks
					.filter((task) => task.task_group === selectedMatter.task_group)
					.map((task) => ({
						task: task.task,
						desc: task.task_description,
						task_group: task.task_group,
						combined: `${task.task} - ${task.task_description}`,
					}));
			}

			// Default: show all tasks for other firms
			return tasks.map((task) => ({
				task: task.task,
				desc: task.task_description,
				task_group: task.task_group,
				combined: `${task.task} - ${task.task_description}`,
			}));
		}, [tasks, matters, formValues.matter_id]);

		const filteredTasks = useMemo(() => {
			// Deduplicate tasks by their combined display value
			const seen = new Set<string>();
			const uniqueTasks = availableTasks.filter((task) => {
				if (seen.has(task.combined)) return false;
				seen.add(task.combined);
				return true;
			});

			// For Naman Howell: sort L codes first, E codes last
			if (isNamanHowell()) {
				uniqueTasks.sort((a, b) => {
					const aIsL = a.task.startsWith("L");
					const bIsL = b.task.startsWith("L");
					const aIsE = a.task.startsWith("E");
					const bIsE = b.task.startsWith("E");
					if (aIsL && !bIsL) return -1;
					if (!aIsL && bIsL) return 1;
					if (aIsE && !bIsE) return 1;
					if (!aIsE && bIsE) return -1;
					return 0;
				});
			}

			if (!taskSearchTerm) {
				return uniqueTasks;
			}
			return uniqueTasks.filter(
				(task) =>
					task.task.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
					task.desc.toLowerCase().includes(taskSearchTerm.toLowerCase()),
			);
		}, [taskSearchTerm, availableTasks]);

		// For Naman Howell: hide E codes by default, show them only when searching
		const taskMaxItems = useMemo(() => {
			if (!isNamanHowell()) return 100;
			return filteredTasks.filter((t) => !t.task.startsWith("E")).length || 100;
		}, [filteredTasks]);

		const availableActivities = useMemo(() => {
			// For Preg O'Donnell: filter activities by the selected matter's activity_group
			if (isPregODonnell()) {
				const selectedMatter = matters.find(
					(matter) => matter.matter_id === formValues.matter_id,
				);

				// Case 1: No matter selected - show "ABA" group as default
				if (!selectedMatter) {
					return activities
						.filter((activity) => activity.activity_group === "ABA")
						.map((activity) => ({
							activity: activity.activity,
							desc: activity.activity_description,
							activity_group: activity.activity_group,
							combined: `${activity.activity} - ${activity.activity_description}`,
						}));
				}

				// Case 2: Matter selected but activity_group is NULL/empty - return empty (no activity codes)
				if (
					!selectedMatter.activity_group ||
					selectedMatter.activity_group.trim() === ""
				) {
					return [];
				}

				// Case 3: Matter selected with valid activity_group - show only that group
				return activities
					.filter(
						(activity) =>
							activity.activity_group === selectedMatter.activity_group,
					)
					.map((activity) => ({
						activity: activity.activity,
						desc: activity.activity_description,
						activity_group: activity.activity_group,
						combined: `${activity.activity} - ${activity.activity_description}`,
					}));
			}

			// For Taylor Day: filter activities by the selected matter's activity_group
			if (isTaylorDay()) {
				const selectedMatter = matters.find(
					(matter) => matter.matter_id === formValues.matter_id,
				);

				// Case 1: No matter selected - show default activity_group
				if (!selectedMatter) {
					return activities
						.filter(
							(activity) =>
								activity.activity_group === TAYLOR_DAY_DEFAULT_ACTIVITY_GROUP,
						)
						.map((activity) => ({
							activity: activity.activity,
							desc: activity.activity_description,
							activity_group: activity.activity_group,
							combined: `${activity.activity} - ${activity.activity_description}`,
						}));
				}

				// Case 2: Matter selected but activity_group is NULL/empty - return empty (activity not required)
				if (
					!selectedMatter.activity_group ||
					selectedMatter.activity_group.trim() === ""
				) {
					return [];
				}

				// Case 3: Matter selected with valid activity_group - show only that group
				return activities
					.filter(
						(activity) =>
							activity.activity_group === selectedMatter.activity_group,
					)
					.map((activity) => ({
						activity: activity.activity,
						desc: activity.activity_description,
						activity_group: activity.activity_group,
						combined: `${activity.activity} - ${activity.activity_description}`,
					}));
			}

			// For Naman Howell: activity is always available (optional or required based on task_group)
			if (isNamanHowell()) {
				const selectedMatter = matters.find(
					(matter) => matter.matter_id === formValues.matter_id,
				);

				// Filter by activity_group if matter has one, otherwise show all
				if (
					selectedMatter?.activity_group &&
					selectedMatter.activity_group.trim() !== ""
				) {
					return activities
						.filter(
							(activity) =>
								activity.activity_group === selectedMatter.activity_group,
						)
						.map((activity) => ({
							activity: activity.activity,
							desc: activity.activity_description,
							activity_group: activity.activity_group,
							combined: `${activity.activity} - ${activity.activity_description}`,
						}));
				}

				// No matter or no activity_group - show all activities
				return activities.map((activity) => ({
					activity: activity.activity,
					desc: activity.activity_description,
					activity_group: activity.activity_group,
					combined: `${activity.activity} - ${activity.activity_description}`,
				}));
			}

			// Default: show all activities for other firms
			return activities.map((activity) => ({
				activity: activity.activity,
				desc: activity.activity_description,
				activity_group: activity.activity_group,
				combined: `${activity.activity} - ${activity.activity_description}`,
			}));
		}, [activities, matters, formValues.matter_id]);

		const filteredActivities = useMemo(() => {
			if (!activitySearchTerm) {
				return availableActivities;
			}

			// Search within availableActivities only (no cross-group searching)
			return availableActivities.filter(
				(activity) =>
					activity.activity
						.toLowerCase()
						.includes(activitySearchTerm.toLowerCase()) ||
					activity.desc
						.toLowerCase()
						.includes(activitySearchTerm.toLowerCase()),
			);
		}, [activitySearchTerm, availableActivities]);

		// Determine if activity is required for the currently selected matter
		const activityRequiredForCurrentMatter = useMemo(() => {
			return isActivityRequired(formValues.matter_id, matters);
		}, [formValues.matter_id, matters]);

		// Determine if task is required for the currently selected matter
		const taskRequiredForCurrentMatter = useMemo(() => {
			return isTaskRequired(formValues.matter_id, matters);
		}, [formValues.matter_id, matters]);

		const handleMatterChange = (value: string) => {
			const selectedMatter = availableMatters.find(
				(matter) => matter.matter_id === value,
			);

			if (!selectedMatter) {
				// Debug: Matter not found in availableMatters - this causes state sync issue
				logger.warn(
					"handleMatterChange: matter not found in availableMatters",
					{
						searchedMatterId: value,
						availableMattersCount: availableMatters.length,
					},
				);
				return false; // Return false to indicate failure
			}

			const matterValue = selectedMatter.name;
			const matterIdValue = selectedMatter.matter_id;

			if (matterIdValue === formValues.matter_id) {
				setFormValues((prev) => {
					return { ...prev, matter: "", matter_id: "" };
				});
				return true; // Toggling off is a valid operation
			}

			// Check if the new matter requires activity codes
			const newMatterRequiresActivity = isActivityRequired(
				matterIdValue,
				matters,
			);

			// If the new matter doesn't require activity, clear any existing activity selection
			const shouldClearActivity = !newMatterRequiresActivity;

			// Check if the new matter requires task codes (Taylor Day only)
			const newMatterRequiresTask = isTaskRequired(matterIdValue, matters);

			// If the new matter doesn't require task, clear any existing task selection
			const shouldClearTask = !newMatterRequiresTask;

			// Determine unit increment for new matter and adjust units if needed
			const newIsZurich = isZurichMatter(selectedMatter.client);
			const newStep = newIsZurich ? 0.05 : 0.1;

			// Check if the previous matter was Zurich to determine the old step
			const prevMatter = matters.find(
				(m) => m.matter_id === formValues.matter_id,
			);
			const prevIsZurich = isZurichMatter(prevMatter?.client || "");

			setFormValues((prev) => {
				// Adjust duration to match new matter's increment
				let adjustedDuration = prev.duration;
				if (typeof prev.duration === "number" && prev.duration > 0) {
					// Special case: if switching from non-Zurich minimum (0.1) to Zurich,
					// use Zurich minimum (0.05) - "minimum should stay minimum"
					if (
						!prevIsZurich &&
						newIsZurich &&
						Math.abs(prev.duration - 0.1) < 0.001
					) {
						adjustedDuration = 0.05;
					} else {
						// Always round UP to next increment (0.05 -> 0.1, 1.25 -> 1.3)
						// First fix floating-point precision on the ratio, then ceil
						const ratio = Math.round((prev.duration / newStep) * 100) / 100;
						adjustedDuration = Math.ceil(ratio) * newStep;
						// Ensure minimum
						if (adjustedDuration < newStep) {
							adjustedDuration = newStep;
						}
						// Fix floating point precision (e.g., 0.30000000000000004 -> 0.3)
						adjustedDuration = Math.round(adjustedDuration * 100) / 100;
					}
				}

				return {
					...prev,
					matter: matterValue,
					matter_id: matterIdValue,
					duration: adjustedDuration,
					...(shouldClearActivity ? { activity: "" } : {}),
					...(shouldClearTask ? { task: "" } : {}),
				};
			});

			// Clear the activity display value if matter doesn't require activity
			if (shouldClearActivity) {
				setActivityValue("");
			}

			// Clear the task display value if matter doesn't require task
			if (shouldClearTask) {
				setTaskValue("");
			}

			return true; // Return true to indicate success
		};

		const [value, setValue] = React.useState("");
		const [taskValue, setTaskValue] = React.useState("");
		const [activityValue, setActivityValue] = React.useState("");

		const handleTaskChange = (value: string) => {
			const taskValue = availableTasks.find(
				(task) => task.combined === value,
			)!.task;

			if (taskValue === formValues.task) {
				setFormValues((prev) => {
					return { ...prev, task: "" };
				});
				return;
			}

			setFormValues((prev) => {
				return { ...prev, task: taskValue };
			});
		};

		const handleActivityChange = (value: string) => {
			const activityValue = availableActivities.find(
				(activity) => activity.combined === value,
			)!.activity;

			if (activityValue === formValues.activity) {
				setFormValues((prev) => {
					return { ...prev, activity: "" };
				});
				return;
			}

			setFormValues((prev) => {
				return { ...prev, activity: activityValue };
			});
		};

		const textareaRef = React.useRef<HTMLTextAreaElement>(null);

		const handleDescriptionChange = (
			e: React.ChangeEvent<HTMLTextAreaElement>,
		) => {
			const textarea = textareaRef.current;
			if (!textarea) return;

			let { value } = e.target;

			const cursorPos = textarea.selectionStart;
			const beforeCursor = value.slice(0, cursorPos);
			const afterCursor = value.slice(cursorPos);

			const match = beforeCursor.match(/(\b\w+)\s$/);

			if (match && settings.abbreviations_enabled) {
				const abbreviation = match[1].toLowerCase();
				const replacement = getAbbreviations()[abbreviation];

				if (replacement) {
					const startOfWord = beforeCursor.lastIndexOf(match[1]);
					const newBeforeCursor =
						beforeCursor.slice(0, startOfWord) + replacement + " ";

					const newValue = newBeforeCursor + afterCursor;

					setFormValues((prev) => ({
						...prev,
						description: newValue,
					}));

					requestAnimationFrame(() => {
						const newCursorPos = newBeforeCursor.length;
						textarea.setSelectionRange(newCursorPos, newCursorPos);
					});

					return;
				}
			}

			setFormValues((prev) => ({
				...prev,
				description: value,
			}));
		};

		const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;

			// Store raw string while typing to allow "0.", "0.0", "0.05" etc.
			// Parsing/rounding happens on blur, not during typing
			setFormValues((prev) => ({ ...prev, duration: value as any }));
		};

		const handleDurationBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			const rawValue = parseFloat(e.target.value);
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);
			const step = isZurichMatter(selectedMatter?.client || "") ? 0.05 : 0.1;

			// If invalid or empty, set to 0.1 (default)
			if (isNaN(rawValue) || e.target.value === "" || rawValue <= 0) {
				setFormValues((prev) => ({ ...prev, duration: step }));
				return;
			}

			// Round UP to next valid increment (0.05 -> 0.1, 1.25 -> 1.3)
			const ratio = Math.round((rawValue / step) * 100) / 100;
			let rounded = Math.ceil(ratio) * step;
			rounded = Math.round(rounded * 100) / 100; // Fix precision (e.g., 0.30000000004 -> 0.3)

			// Ensure minimum
			if (rounded < step) {
				rounded = step;
			}

			setFormValues((prev) => ({ ...prev, duration: rounded }));
		};

		const handleDurationIncrement = () => {
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);
			const step = isZurichMatter(selectedMatter?.client || "") ? 0.05 : 0.1;

			const currentValue =
				typeof formValues.duration === "number"
					? formValues.duration
					: parseFloat(String(formValues.duration)) || 0;

			const newValue = Math.round((currentValue + step) * 100) / 100;
			setFormValues((prev) => ({ ...prev, duration: newValue }));
		};

		const handleDurationDecrement = () => {
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);
			const step = isZurichMatter(selectedMatter?.client || "") ? 0.05 : 0.1;

			const currentValue =
				typeof formValues.duration === "number"
					? formValues.duration
					: parseFloat(String(formValues.duration)) || 0;

			const newValue = Math.max(
				step,
				Math.round((currentValue - step) * 100) / 100,
			);
			setFormValues((prev) => ({ ...prev, duration: newValue }));
		};

		const handleGenerateDescription = async () => {
			if (formValues.description.trim().length < 20) {
				toast.error("Please enter a longer description.", {
					position: "top-center",
					duration: 3000,
				} as any);
				return;
			}

			// Save current description for undo
			const descriptionBeforeGeneration = formValues.description;
			setIsGenerating(true);

			try {
				const generatedDescription = await logger.trackOperation(
					"AI narrative generation",
					async () => {
						const response = await makeApiRequest({
							endpoint: "/records/generateNewDescription",
							method: "POST",
							body: {
								description: formValues.description,
								date: formValues.date,
							},
						});

						if (response.error) {
							throw new Error(response.error);
						}

						if (
							!response.data ||
							typeof response.data !== "string" ||
							!response.data.trim()
						) {
							throw new Error("Invalid response from AI generation");
						}

						return response.data;
					},
					{ inputLength: formValues.description.length },
					3000, // Warn if AI takes longer than 3 seconds
				);

				// Store previous description for undo functionality
				setPreviousDescription(descriptionBeforeGeneration);

				setFormValues((prev) => ({
					...prev,
					description: generatedDescription,
				}));
				setAiGenerationUsed(true); // Track that AI was used

				toast.success("Description generated with AI!", {
					position: "top-center",
					duration: 3000,
				} as any);
			} catch (error) {
				if (error instanceof Error) {
					logger.captureException(error, {
						action: "generateDescription",
					});
				}
				toast.error("Failed to generate description. Please try manually.", {
					position: "top-center",
					duration: 3000,
				} as any);
			} finally {
				setIsGenerating(false);
			}
		};

		const handleUndoGeneration = () => {
			if (previousDescription !== null) {
				setFormValues((prev) => ({
					...prev,
					description: previousDescription,
				}));
				setPreviousDescription(null);
				toast.success("Reverted to previous narrative", {
					position: "top-center",
					duration: 2000,
				} as any);
			}
		};

		const handleFixRedWords = async (originalDescription: string) => {
			try {
				setIsFixingRedWords(true);

				const response = await makeApiRequest({
					endpoint: "/records/redWords",
					method: "POST",
					body: {
						description: originalDescription,
						date: formValues.date,
					},
				});

				if (response.error) {
					throw new Error(response.error);
				}

				if (!response.data) {
					throw new Error("Invalid response format from red words API");
				}

				const fixedDescription = response.data;

				// Update the form with the fixed description
				if (typeof fixedDescription === "string" && fixedDescription.trim()) {
					setFormValues((prev) => ({
						...prev,
						description: fixedDescription,
					}));
				} else {
					throw new Error("Invalid fixed description received");
				}

				// Close the dialog and clear pending save
				setShowRedWordDialog(false);
				setPendingSave(null);

				toast.success("Red words have been automatically replaced!", {
					position: "top-center",
					duration: 3000,
				} as any);
			} catch (error) {
				logger.error("Red words fix failed", {
					error: error instanceof Error ? error.message : String(error),
				});
				if (error instanceof Error) {
					logger.captureException(error, { action: "fixRedWords" });
				}
				toast.error("Failed to fix red words. Please try manually.", {
					position: "top-center",
					duration: 3000,
				} as any);
			} finally {
				setIsFixingRedWords(false);
			}
		};

		const handleConfirmWithRedWords = () => {
			if (pendingSave) {
				const { shouldExport } = pendingSave;

				setValue("");
				setTaskValue("");
				setActivityValue("");

				onSave(formValues, shouldExport); // Save the new entry
				setOpen(false);
				setFormValues({
					id: 0,
					date: "",
					matter: "",
					matter_id: "",
					description: "",
					duration: 0,
					activity: "",
					task: "",
				});
				setDate(currentDate || new Date()); // Reset date to currentDate or today
				setShowRedWordDialog(false);
				setPendingSave(null);
				setAiGenerationUsed(false); // Reset for next entry
				setPreviousDescription(null); // Clear undo state
			}
		};

		// Shared validation logic - returns true if valid, false if invalid (and shows toast)
		const validateForm = (action: "save" | "duplicate"): boolean => {
			const { matter, matter_id, description, duration, task, activity } =
				formValues;
			formValues.date = date ? format(date!, "MM/dd/yyyy") : "";

			// Use the cached useMemo values for consistency with the UI
			const taskRequired = taskRequiredForCurrentMatter;
			const activityRequired = activityRequiredForCurrentMatter;

			if (
				!date ||
				!matter ||
				!description ||
				!duration ||
				(taskRequired && !task) ||
				(activityRequired && !activity) ||
				description === "Please fill in the narrative."
			) {
				logger.captureException(
					new Error(`Add entry validation failed - missing fields (${action})`),
					{
						action,
						hasDate: !!date,
						hasMatter: !!matter,
						hasMatterId: !!matter_id,
						displayValue: value,
						displayValueMatchesFormMatterId: value === matter_id,
						stateOutOfSync: !!value && (!matter || !matter_id),
						hasDescription:
							!!description && description !== "Please fill in the narrative.",
						hasDuration: !!duration,
						hasTask: !!task,
						taskRequired,
						hasActivity: !!activity,
						activityRequired,
						descriptionLength: description?.length || 0,
						aiGenerationUsed,
						availableMattersCount: availableMatters.length,
					},
				);
				toast.error("Please fill out all fields", {
					position: "top-center",
					duration: 2500,
				});
				return false;
			}

			const today = new Date();

			if (!date) {
				logger.warn(
					`Add entry validation failed - date unexpectedly null (${action})`,
				);
				toast.error("Please select a date");
				return false;
			}

			if (date > today) {
				logger.warn(`Add entry validation failed - future date (${action})`, {
					selectedDate: date.toISOString(),
					today: today.toISOString(),
				});
				toast.error("Record date cannot be in the future");
				return false;
			}

			if (duration < 0) {
				logger.warn(
					`Add entry validation failed - negative duration (${action})`,
					{ duration },
				);
				toast.error("Please enter valid time", {
					position: "top-center",
					duration: 2500,
				});
				return false;
			}

			// Validate units match required increment (0.05 for Zurich, 0.1 for others)
			const selectedMatter = matters.find((m) => m.matter_id === matter_id);
			const requiredStep = isZurichMatter(selectedMatter?.client || "")
				? 0.05
				: 0.1;
			const remainder = Math.round((duration % requiredStep) * 1000) / 1000;
			if (remainder !== 0 && Math.abs(remainder - requiredStep) > 0.001) {
				logger.warn(
					`Add entry validation failed - invalid unit increment (${action})`,
					{
						duration,
						requiredStep,
						remainder,
					},
				);
				toast.error(`Units must be in increments of ${requiredStep}`, {
					position: "top-center",
					duration: 2500,
				});
				return false;
			}

			return true;
		};

		// Prepare form values for submission (clears optional fields if not required)
		const prepareFormValues = () => {
			const taskRequired = taskRequiredForCurrentMatter;
			const activityRequired = activityRequiredForCurrentMatter;

			let finalFormValues = { ...formValues };
			// For Naman Howell: preserve activity even when optional (user may have selected one)
			if (!activityRequired && !isNamanHowell()) {
				finalFormValues = { ...finalFormValues, activity: "" };
			}
			if (!taskRequired) {
				finalFormValues = { ...finalFormValues, task: "" };
			}
			return finalFormValues;
		};

		const validateAndSave = (shouldExport: boolean) => {
			if (!validateForm("save")) return;

			const { description } = formValues;

			// Check for red words if restrictions apply
			if (shouldHighlightRedWords && hasRedWords(description)) {
				const redWordMatches = detectRedWords(description);
				setPendingSave({ shouldExport, redWordMatches });
				setShowRedWordDialog(true);
				return;
			}

			// Clear display values and save
			setValue("");
			setTaskValue("");
			setActivityValue("");

			const finalFormValues = prepareFormValues();
			onSave(finalFormValues, shouldExport);
			setOpen(false);
			setFormValues({
				id: 0,
				date: "",
				matter: "",
				matter_id: "",
				description: "",
				duration: 0,
				activity: "",
				task: "",
			});
			setDate(currentDate || new Date());
			setAiGenerationUsed(false);
			setPreviousDescription(null); // Clear undo state
		};

		// Duplicate: creates entry but keeps form filled for quick re-entry
		const handleDuplicate = () => {
			if (!validateForm("duplicate")) return;

			setIsDuplicating(true);
			try {
				const finalFormValues = prepareFormValues();
				onSave(finalFormValues, false); // Create entry (not exported)
				// Toast handled by parent (onSave callback) - keep form filled, keep dialog open
			} finally {
				setIsDuplicating(false);
			}
		};

		return (
			<Dialog open={open} onOpenChange={setOpen}>
				{showTrigger && (
					<DialogTrigger asChild>
						<Button
							variant="outline"
							onClick={() => setOpen(true)}
							className={cn(
								darkMode
									? "border border-solid border-[#3e4451] text-[#abb2bf] bg-[#21252b] hover:bg-[#3e4451] hover:text-[#abb2bf]"
									: "border border-solid border-gray-300 bg-white text-black",
							)}
						>
							Add
						</Button>
					</DialogTrigger>
				)}
				<DialogContent
					className={cn(
						"sm:max-w-[525px]",
						darkMode ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]" : "",
					)}
				>
					<DialogHeader>
						{/* [REF001]: Applied 'font-sans' related to h1, h2, h3 force change. */}
						<DialogTitle className="font-sans">Add entry</DialogTitle>
						<DialogDescription>
							Manually submit an entry here.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid w-full gap-1.5">
							<Label htmlFor="username">Matter</Label>
							<SearchablePicker
								items={filteredMatters}
								value={value}
								onChange={(matterId) => {
									const success = handleMatterChange(matterId);
									if (success) {
										setValue(matterId);
									}
								}}
								getValue={(m) => m.matter_id}
								getLabel={(m) => m.combinedTrimmed}
								placeholder="Select matter..."
								searchPlaceholder="Search matter..."
								emptyMessage="No matter found."
								searchTerm={searchTerm}
								onSearchChange={setSearchTerm}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="grid w-full gap-1.5">
								<Label htmlFor="username">Task</Label>
								<SearchablePicker
									items={filteredTasks}
									value={taskValue}
									onChange={(newValue) => {
										setTaskValue(newValue);
										handleTaskChange(newValue);
									}}
									getValue={(t) => t.combined}
									getLabel={(t) => `${t.task} - ${t.desc}`}
									placeholder="Select task code..."
									searchPlaceholder="Search task..."
									emptyMessage="No task found."
									disabled={!taskRequiredForCurrentMatter}
									disabledMessage="Not required"
									popoverWidth="280px"
									searchTerm={taskSearchTerm}
									onSearchChange={setTaskSearchTerm}
									maxItemsBeforeSearch={taskMaxItems}
								/>
							</div>
							<div className="grid w-full gap-1.5">
								<Label htmlFor="username">Activity</Label>
								<SearchablePicker
									items={filteredActivities}
									value={activityValue}
									onChange={(newValue) => {
										setActivityValue(newValue);
										handleActivityChange(newValue);
									}}
									getValue={(a) => a.combined}
									getLabel={(a) => `${a.activity} - ${a.desc}`}
									placeholder="Select activity code..."
									searchPlaceholder="Search activity..."
									emptyMessage="No activity found."
									disabled={!activityRequiredForCurrentMatter && !isNamanHowell()}
									disabledMessage="Not required"
									popoverWidth="280px"
									searchTerm={activitySearchTerm}
									onSearchChange={setActivitySearchTerm}
									maxItemsBeforeSearch={100}
								/>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="grid w-full gap-1.5">
								<Label htmlFor="date">Date</Label>
								<Popover>
									<PopoverTrigger asChild>
										<Button
											variant={"outline"}
											className={cn(
												"w-full justify-start text-left font-normal overflow-hidden",
												darkMode
													? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]" // Dark mode hover state
													: "hover:bg-gray-100", // Light mode hover state
												!date && "text-muted-foreground",
											)}
										>
											<CalendarIcon className="mr-2 h-4 w-4" />
											{date ? format(date, "PP") : <span>Pick a date</span>}
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-auto p-0">
										<Calendar
											mode="single"
											selected={date}
											onSelect={(day) => setDate(day || currentDate)}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							</div>
							<div className="grid w-full gap-1.5">
								<Label htmlFor="number">Units</Label>
								<div className="flex gap-1">
									<Input
										id="duration"
										value={
											!formValues.duration || formValues.duration === 0
												? ""
												: typeof formValues.duration === "number" ||
													  typeof formValues.duration === "string"
													? formValues.duration
													: ""
										}
										className={cn(
											"col-span-3",
											darkMode
												? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
												: "",
										)}
										type="text"
										inputMode="decimal"
										autoComplete="off"
										onChange={handleDurationChange}
										onBlur={handleDurationBlur}
									/>
									<div className="flex flex-col gap-0.5">
										<Button
											type="button"
											variant="outline"
											size="icon"
											onClick={handleDurationIncrement}
											className={cn(
												"h-[18px] w-7 p-0",
												darkMode
													? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
													: "hover:bg-gray-100",
											)}
										>
											<ChevronUp className="h-3 w-3" />
										</Button>
										<Button
											type="button"
											variant="outline"
											size="icon"
											onClick={handleDurationDecrement}
											className={cn(
												"h-[18px] w-7 p-0",
												darkMode
													? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
													: "hover:bg-gray-100",
											)}
										>
											<ChevronDown className="h-3 w-3" />
										</Button>
									</div>
								</div>
							</div>
						</div>
						<div className="grid w-full gap-1.5">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-1.5">
									<Label htmlFor="message">Narrative</Label>
									{settings.abbreviations_enabled && (
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														type="button"
														className="text-muted-foreground hover:text-foreground"
														aria-label="Abbreviation help"
													>
														<HelpCircle className="h-4 w-4" />
													</button>
												</TooltipTrigger>
												<TooltipContent
													side="right"
													className="px-2 py-1 text-xs"
												>
													<p>Abbreviations enabled</p>
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									)}
								</div>
								<div className="flex items-center gap-2">
									{previousDescription !== null && (
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={handleUndoGeneration}
											className={cn(
												"flex items-center gap-1 h-8 text-xs",
												darkMode
													? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
													: "hover:bg-gray-100",
											)}
										>
											<Undo2 className="h-3 w-3" />
											Undo
										</Button>
									)}
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={handleGenerateDescription}
										disabled={isGenerating || !formValues.description.trim()}
										className={cn(
											"flex items-center gap-2 h-8 text-xs",
											darkMode
												? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
												: "hover:bg-gray-100",
										)}
									>
										<Sparkles className="h-3 w-3" />
										{isGenerating ? "Generating..." : "Generate with AI"}
									</Button>
								</div>
							</div>
							<RedWordTextarea
								placeholder="Type your task description here."
								id="message"
								onChange={handleDescriptionChange}
								textareaRef={textareaRef}
								value={formValues.description}
								shouldHighlight={shouldHighlightRedWords}
								className={cn(
									darkMode
										? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
										: "",
								)}
								style={{ minHeight: "100px" }}
							/>
						</div>
					</div>
					<DialogFooter className="sm:justify-between">
						{/* Left: Duplicate button (icon-only) */}
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="outline"
										size="icon"
										onClick={handleDuplicate}
										disabled={isDuplicating}
										className="h-10 w-10 border-[hsl(var(--brand-alt))] bg-transparent text-[hsl(var(--brand-alt))] hover:bg-[hsl(var(--brand-alt))]/10"
									>
										<Copy className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>Duplicate</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>

						{/* Right: Save buttons (horizontal) */}
						<div className="flex flex-row gap-2">
							<Button
								type="submit"
								variant="outline"
								onClick={() => validateAndSave(true)}
								className="border-[hsl(var(--brand-alt))] bg-transparent text-[hsl(var(--brand-alt))] hover:bg-[hsl(var(--brand-alt))]/10"
							>
								Add and export
							</Button>
							<Button
								type="submit"
								onClick={() => validateAndSave(false)}
								className="border-[hsl(var(--brand-alt))] bg-[hsl(var(--brand-alt))] text-white hover:bg-[hsl(var(--brand-alt-hover))]"
							>
								Add entry
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>

				<RedWordConfirmationDialog
					open={showRedWordDialog}
					onOpenChange={setShowRedWordDialog}
					redWordMatches={pendingSave?.redWordMatches || []}
					onConfirm={handleConfirmWithRedWords}
					onCancel={() => {
						setShowRedWordDialog(false);
						setPendingSave(null);
						setIsFixingRedWords(false);
					}}
					onFixForMe={handleFixRedWords}
					originalDescription={formValues.description}
					darkMode={darkMode}
					action="add"
					isFixing={isFixingRedWords}
				/>
			</Dialog>
		);
	},
);
export default AddEntryDialog;
